import type { z } from 'zod'

import { translate } from '../i18n/index.js'
import { SERVER_MESSAGE_KEYS } from '../i18n/messages/common.js'
import { DOMAIN_SERVER_MESSAGE_KEYS } from '../i18n/messages/domains.js'
import { SITE_SERVER_MESSAGE_KEYS } from '../i18n/messages/siteDetail.js'

/** Typed API failure. `issues` carries field errors for 422 responses. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: { path: (string | number)[]; message: string }[] = [],
  ) {
    super(message)
    this.name = 'ApiError'
  }

  fieldErrors(): Record<string, string> {
    return Object.fromEntries(this.issues.map((i) => [i.path.join('.'), i.message]))
  }
}

type Options = { method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'; body?: unknown; signal?: AbortSignal }

const unauthorizedListeners = new Set<() => void>()
export const onUnauthorized = (fn: () => void) => {
  unauthorizedListeners.add(fn)
  return () => {
    unauthorizedListeners.delete(fn)
  }
}

async function request(path: string, opts: Options): Promise<Response> {
  const res = await fetch(`/api${path}`, {
    method: opts.method ?? 'GET',
    credentials: 'same-origin',
    headers: opts.body !== undefined ? { 'content-type': 'application/json' } : {},
    ...(opts.body !== undefined ? { body: JSON.stringify(opts.body) } : {}),
    ...(opts.signal ? { signal: opts.signal } : {}),
  })
  if (!res.ok) {
    const json = (await res.json().catch(() => null)) as { error?: { code?: string; message?: string; issues?: { path: (string | number)[]; message: string }[] } } | null
    // A failed sign-in step (password, code, set-up before a session) is answered on the sign-in page itself.
    if (res.status === 401 && !path.startsWith('/auth/login') && !path.startsWith('/auth/2fa/verify') && !path.startsWith('/auth/2fa/pending/')) unauthorizedListeners.forEach((fn) => fn())
    if (json?.error?.message) throw new ApiError(res.status, json.error.code ?? 'internal_error', json.error.message, Array.isArray(json.error.issues) ? json.error.issues : [])
    throw new ApiError(res.status, 'internal_error', translate('common.requestFailed', { status: res.status }))
  }
  return res
}

export async function api<T extends z.ZodType>(path: string, schema: T, opts: Options = {}): Promise<z.infer<T>> {
  return schema.parse(await (await request(path, opts)).json())
}

export async function apiVoid(path: string, opts: Options = {}): Promise<void> {
  await request(path, opts)
}

/** Message for the user: the API's fixed messages are translated, anything else is shown as sent. */
export const errorMessage = (err: unknown) => {
  const message = err instanceof Error ? err.message : ''
  const key = SERVER_MESSAGE_KEYS[message] ?? SITE_SERVER_MESSAGE_KEYS[message] ?? DOMAIN_SERVER_MESSAGE_KEYS[message]
  if (key) return translate(key)
  if (/^The site answered \d+$/.test(message)) return translate('apiError.siteRejected')
  return message || translate('common.somethingWentWrong')
}
